import React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import colorPalette from "./colorPallete";
import themeConfig from "../../theme/themeConfig.json";

declare module "@mui/material/styles" {
  interface Palette {
    white: Palette["primary"];
    black: Palette["primary"];
    tomato: Palette["primary"];
  }
  interface PaletteOptions {
    white?: PaletteOptions["primary"];
    black?: PaletteOptions["primary"];
    tomato?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    dashed: true;
  }
  interface ButtonPropsColorOverrides {
    white: true;
    black: true;
    tomato: true;
  }
}

declare module "@mui/lab/LoadingButton" {
  interface LoadingButtonPropsColorOverrides {
    white: true;
    black: true;
    tomato: true;
  }
}

const MAIN_BACKGROUND_COLOR = themeConfig.colors.surface.subtle;

const theme = createTheme({
  palette: {
    ...colorPalette,
    success: {
      main: themeConfig.colors.success.DEFAULT,
      contrastText: themeConfig.colors.success.foreground,
    },
    info: {
      main: themeConfig.colors.info.DEFAULT,
      contrastText: themeConfig.colors.info.foreground,
    },
    warning: {
      main: themeConfig.colors.warning.DEFAULT,
      contrastText: themeConfig.colors.warning.foreground,
    },
    error: {
      main: themeConfig.colors.danger.DEFAULT,
      contrastText: themeConfig.colors.danger.foreground,
    },
    white: colorPalette.white,
    black: colorPalette.black,
    tomato: colorPalette.tomato,
  },
  typography: {
    fontFamily: [
      "Poppins",
      "Lato",
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    allVariants: {
      marginTop: 0,
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h1: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.25 },
    h2: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.35 },
    h4: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: "1.15rem", fontWeight: 600, lineHeight: 1.45 },
    h6: { fontSize: "1.075rem", fontWeight: 600, lineHeight: 1.5 },
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
    button: { fontWeight: 600 },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        gutterBottom: { marginBottom: "1rem" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            boxShadow: `inset 0 0 0 2px ${alpha(colorPalette.primary[500], 0.3)}`,
          },
          "&.Mui-error": {
            boxShadow: `inset 0 0 0 2px ${alpha(themeConfig.colors.danger.DEFAULT, 0.35)}`,
          },
          "&.Mui-focused.Mui-error": {
            boxShadow: `inset 0 0 0 2px ${alpha(themeConfig.colors.danger.DEFAULT, 0.35)}`,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: { margin: 0 },
        selectLabel: { margin: 0 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 10px 30px 0 rgb(82 63 105 / 5%)",
          backgroundColor: colorPalette.white.main,
          color: colorPalette.grey[700],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "0.475rem",
          padding: "calc(0.55rem + 1px) calc(1.25rem + 1px)",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&.bg-brand": {
            color: themeConfig.colors.brand.foreground,
          },
          "&.bg-success": {
            color: themeConfig.colors.success.foreground,
          },
          "&.bg-danger": {
            color: themeConfig.colors.danger.foreground,
          },
          "&.bg-info": {
            color: themeConfig.colors.info.foreground,
          },
          "&.Mui-disabled.MuiButton-containedPrimary": {
            backgroundColor: alpha(colorPalette.primary[500], 0.5),
            color: colorPalette.white.main,
          },
          "&.Mui-disabled.MuiButton-outlinedPrimary": {
            borderColor: alpha(colorPalette.primary[500], 0.4),
            color: alpha(colorPalette.primary[500], 0.6),
          },
          "&.Mui-disabled.MuiButton-textPrimary": {
            color: alpha(colorPalette.primary[500], 0.6),
          },
        },
      },
      variants: [
        {
          props: { variant: "dashed" },
          style: {
            textTransform: "none",
            border: `2px dashed ${colorPalette.primary[500]}`,
          },
        },
        {
          props: { variant: "dashed", color: "secondary" },
          style: {
            border: `4px dashed ${colorPalette.red[500]}`,
          },
        },
        {
          props: { variant: "outlined", color: "white" },
          style: {
            border: "1px solid white",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          },
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 500,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});

const ThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{ body: { backgroundColor: MAIN_BACKGROUND_COLOR } }}
        />
        {children}
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default ThemeWrapper;
